import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router";
import { tenantWorkspaceApi } from "@/core/api/tenantWorkspaceApi";
import { tenantFeaturesApi } from "@/core/api/tenantFeaturesApi";
import { tenantFeatureCodes } from "@/core/types/features";
import type {
  RetailInvoice,
  RetailPaymentMethod,
  RetailPaymentStatus,
  RetailProduct,
} from "@/core/types/tenantWorkspace";
import { createUuid } from "@/core/utils/uuid";
import { getErrorMessage } from "@/core/utils/errors";
import { formatDateTime } from "@/core/utils/date";
import { AppIcon } from "@/shared/components/AppIcon";

type Mode = "introduce" | "sale";
interface SelectedRetailLine {
  key: string;
  product: RetailProduct;
  variantId?: string;
  branchId?: string;
}
const maxSelectedProducts = 20;
const number = (value: number): string =>
  new Intl.NumberFormat("fa-IR").format(value);
const money = (value: number | null, currency = "IRR"): string =>
  value === null
    ? "قیمت ثبت نشده"
    : `${number(value)} ${({ IRR: "ریال", IRT: "تومان", USD: "دلار", EUR: "یورو" } as Record<string, string>)[currency] ?? currency}`;
const itemTypeLabel = (itemType: RetailProduct["itemType"]): string =>
  itemType === "Service" ? "خدمت" : "کالا";
const availabilityLabel: Record<RetailProduct["availabilityStatus"], string> = {
  Available: "موجود",
  OnRequest: "سفارشی",
  OutOfStock: "ناموجود",
  Discontinued: "غیرفعال",
};
const paymentLabels: Record<RetailPaymentMethod, string> = {
  Card: "کارت‌خوان",
  Cash: "نقدی",
  Online: "پرداخت آنلاین",
  Transfer: "واریز بانکی",
  Credit: "اعتباری/نسیه",
  GiftCard: "کارت هدیه",
  Multiple: "ترکیبی",
};
const selectablePaymentMethods: Exclude<RetailPaymentMethod, "Multiple">[] = [
  "Card",
  "Cash",
  "Online",
  "Transfer",
  "Credit",
];
const paymentStatusLabels: Record<RetailPaymentStatus, string> = {
  Paid: "پرداخت‌شده",
  Unpaid: "پرداخت‌نشده",
  PartiallyPaid: "پرداخت ناقص",
  PartiallyRefunded: "مرجوعی جزئی",
  Refunded: "مرجوع‌شده",
};
const orderStatusLabels: Record<RetailInvoice["status"], string> = {
  Completed: "تکمیل‌شده",
  PartiallyReturned: "مرجوعی جزئی",
  Returned: "مرجوع‌شده",
  Cancelled: "لغو شده",
  Voided: "باطل‌شده",
};
const selectedVariant = (line: SelectedRetailLine) =>
  line.variantId
    ? line.product.variants.find((variant) => variant.id === line.variantId)
    : undefined;
const selectedBranch = (line: SelectedRetailLine) =>
  selectedVariant(line)?.stocks.find(
    (stock) => stock.branchId === line.branchId,
  );
const linePrice = (line: SelectedRetailLine): number =>
  selectedVariant(line)?.priceOverride ?? line.product.price ?? 0;
const shelfTones = [
  "from-rose-100 to-orange-50 text-rose-700",
  "from-sky-100 to-cyan-50 text-sky-700",
  "from-violet-100 to-fuchsia-50 text-violet-700",
  "from-emerald-100 to-lime-50 text-emerald-700",
  "from-amber-100 to-yellow-50 text-amber-700",
];

export const RetailSalesPage = (): React.ReactElement => {
  const navigate = useNavigate();
  const searchInput = useRef<HTMLInputElement>(null);
  const customerPanelRef = useRef<HTMLElement>(null);
  const customerMobileInputRef = useRef<HTMLInputElement>(null);
  const saleIdempotencyKey = useRef(createUuid());
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<Mode>("introduce");
  const [search, setSearch] = useState("");
  const [stableSearch, setStableSearch] = useState("");
  const [activeShelf, setActiveShelf] = useState("all");
  const [mobileCheckoutOpen, setMobileCheckoutOpen] = useState(false);
  const [exitWarningOpen, setExitWarningOpen] = useState(false);
  const [selectedLines, setSelectedLines] = useState<SelectedRetailLine[]>([]);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [variantPicker, setVariantPicker] = useState<RetailProduct | null>(
    null,
  );
  const [selectionMessage, setSelectionMessage] = useState<string | null>(null);
  const [interactionTarget, setInteractionTarget] = useState<RetailProduct | null>(null);
  const [interactionMobile, setInteractionMobile] = useState("");
  const [interactionCustomerName, setInteractionCustomerName] = useState("");
  const [mobile, setMobile] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [landingValidityDays, setLandingValidityDays] = useState(7);
  const [paymentMethod, setPaymentMethod] =
    useState<Exclude<RetailPaymentMethod, "Multiple">>("Card");
  const [paymentStatus, setPaymentStatus] =
    useState<Extract<RetailPaymentStatus, "Paid" | "Unpaid" | "PartiallyPaid">>(
      "Paid",
    );
  const [paidAmount, setPaidAmount] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountsOpen, setDiscountsOpen] = useState(false);
  const [missingMobileWarningOpen, setMissingMobileWarningOpen] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [pointsToRedeem, setPointsToRedeem] = useState(0);
  const [giftCardCode, setGiftCardCode] = useState("");
  const [giftCardAmount, setGiftCardAmount] = useState(0);
  const [introductionResult, setIntroductionResult] = useState<{
    url: string;
    error: string | null;
  } | null>(null);
  const [saleResult, setSaleResult] = useState<RetailInvoice | null>(null);
  const [manageInvoice, setManageInvoice] = useState<RetailInvoice | null>(
    null,
  );
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [returnQuantities, setReturnQuantities] = useState<
    Record<string, number>
  >({});
  const [returnMethod, setReturnMethod] =
    useState<Exclude<RetailPaymentMethod, "Multiple">>("Card");
  const [returnReason, setReturnReason] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => setStableSearch(search), 120);
    return () => window.clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    searchInput.current?.focus();
  }, []);
  useEffect(() => {
    const requestedMode = searchParams.get("mode");
    if (requestedMode === "introduce" || requestedMode === "sale") {
      setMode(requestedMode);
    }
  }, [searchParams]);
  useEffect(() => {
    if (
      !interactionTarget &&
      !missingMobileWarningOpen &&
      !mobileCheckoutOpen &&
      !exitWarningOpen
    )
      return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [
    exitWarningOpen,
    interactionTarget,
    missingMobileWarningOpen,
    mobileCheckoutOpen,
  ]);
  const productsQuery = useQuery({
    queryKey: ["retail", "products", stableSearch],
    queryFn: () => tenantWorkspaceApi.retailProducts(stableSearch),
  });
  const recentQuery = useQuery({
    queryKey: ["retail", "recent"],
    queryFn: tenantWorkspaceApi.retailRecent,
  });
  const dashboardQuery = useQuery({
    queryKey: ["retail", "dashboard"],
    queryFn: tenantWorkspaceApi.retailDashboard,
  });
  const featuresQuery = useQuery({
    queryKey: ["tenant-workspace", "features"],
    queryFn: tenantFeaturesApi.current,
  });
  const productInteractionEnabled = featuresQuery.data?.some(
    (feature) => feature.code === tenantFeatureCodes.productInteractionTracking && feature.isEnabled,
  ) ?? false;
  const interactionSettingsQuery = useQuery({
    queryKey: ["tenant-workspace", "product-interaction-settings"],
    queryFn: tenantWorkspaceApi.productInteractionSettings,
    enabled: productInteractionEnabled,
  });
  const interactionSummaryQuery = useQuery({
    queryKey: ["retail", "product-interactions", "summary"],
    queryFn: tenantWorkspaceApi.productInteractionSummary,
    enabled: productInteractionEnabled,
  });
  const invoicesQuery = useQuery({
    queryKey: ["retail", "invoices"],
    queryFn: () => tenantWorkspaceApi.retailInvoices(12),
    enabled: mode === "sale",
  });
  const products = productsQuery.data ?? [];
  const catalogShelves = useMemo(() => {
    const categoryMap = new Map<
      string,
      { title: string; imageUrl: string | null }
    >();
    products.forEach((product) => {
      const key = product.categoryId ?? `type:${product.itemType}`;
      const title = product.categoryName ?? itemTypeLabel(product.itemType);
      const current = categoryMap.get(key);
      if (!current)
        categoryMap.set(key, { title, imageUrl: product.imageUrl });
      else if (!current.imageUrl && product.imageUrl)
        categoryMap.set(key, { ...current, imageUrl: product.imageUrl });
    });
    return Array.from(categoryMap, ([key, value]) => ({ key, ...value }));
  }, [products]);
  const displayedProducts = useMemo(
    () => {
      if (stableSearch) return products;
      if (activeShelf === "favorites")
        return products.filter((product) => product.isFavorite);
      if (activeShelf === "recent") return recentQuery.data ?? [];
      if (activeShelf !== "all")
        return products.filter(
          (product) =>
            (product.categoryId ?? `type:${product.itemType}`) === activeShelf,
        );
      return products;
    },
    [activeShelf, products, recentQuery.data, stableSearch],
  );
  const selectedOrder = useMemo(
    () => new Map(selectedLines.map((line, index) => [line.key, index + 1])),
    [selectedLines],
  );
  const subtotal = useMemo(
    () =>
      selectedLines.reduce(
        (sum, line) => sum + linePrice(line) * (quantities[line.key] ?? 1),
        0,
      ),
    [quantities, selectedLines],
  );
  const safeDiscount = Math.min(Math.max(discountAmount || 0, 0), subtotal);
  const total = subtotal - safeDiscount;
  const activeDiscountOptions = [
    safeDiscount > 0,
    Boolean(discountCode.trim()),
    pointsToRedeem > 0,
    Boolean(giftCardCode.trim()),
  ].filter(Boolean).length;
  const busy = false;

  const refreshAfterAction = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["retail"] }),
      queryClient.invalidateQueries({ queryKey: ["contacts"] }),
    ]);
  };
  const revealCheckoutResult = (): void => {
    setMobileCheckoutOpen(true);
    window.setTimeout(() => {
      const panel = customerPanelRef.current;
      panel?.scrollTo({ top: panel.scrollHeight, behavior: "smooth" });
    }, 100);
  };
  const introduction = useMutation({
    mutationFn: () => {
      const productIds = Array.from(
        new Set(selectedLines.map((line) => line.product.id)),
      );
      return tenantWorkspaceApi.createRetailQuickInteraction({
        productId: productIds[0],
        productIds,
        mobile,
        customerName: customerName || null,
        landingValidityDays,
      });
    },
    onSuccess: async (data) => {
      setIntroductionResult({
        url: data.landingUrl,
        error: data.deliveryError,
      });
      setSaleResult(null);
      setMobile("");
      setCustomerName("");
      setSelectedLines([]);
      setQuantities({});
      revealCheckoutResult();
      await refreshAfterAction();
    },
  });
  const sale = useMutation({
    mutationFn: () =>
      tenantWorkspaceApi.createRetailOneClickSale(
        {
          items: selectedLines.map((line, index) => ({
            catalogItemId: line.product.id,
            quantity: quantities[line.key] ?? 1,
            sortOrder: index,
            variantId: line.variantId ?? null,
            branchId: line.branchId ?? null,
          })),
          mobile: mobile.trim(),
          customerName: customerName.trim() || null,
          paymentMethod,
          paymentStatus,
          paidAmount:
            paymentStatus === "PartiallyPaid"
              ? Math.min(Math.max(paidAmount || 0, 0), total)
              : paymentStatus === "Paid"
                ? total
                : 0,
          discountAmount: safeDiscount,
          discountCode: discountCode.trim() || null,
          pointsToRedeem: Math.max(0, pointsToRedeem || 0),
          giftCardCode: giftCardCode.trim() || null,
          giftCardAmount: giftCardAmount > 0 ? giftCardAmount : null,
        },
        saleIdempotencyKey.current,
      ),
    onSuccess: async (data) => {
      saleIdempotencyKey.current = createUuid();
      setSaleResult(data);
      setIntroductionResult(null);
      setMobile("");
      setCustomerName("");
      setSelectedLines([]);
      setQuantities({});
      setDiscountAmount(0);
      setDiscountsOpen(false);
      setDiscountCode("");
      setPointsToRedeem(0);
      setGiftCardCode("");
      setGiftCardAmount(0);
      setPaidAmount(0);
      setPaymentStatus("Paid");
      revealCheckoutResult();
      await refreshAfterAction();
    },
  });
  const submitSale = (): void => {
    if (!mobile.trim()) {
      setMissingMobileWarningOpen(true);
      return;
    }
    sale.mutate();
  };
  const continueSaleWithoutMobile = (): void => {
    setMissingMobileWarningOpen(false);
    sale.mutate();
  };
  const focusCustomerMobile = (): void => {
    setMissingMobileWarningOpen(false);
    customerPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    window.setTimeout(() => customerMobileInputRef.current?.focus(), 300);
  };
  const favorite = useMutation({
    mutationFn: ({ id, value }: { id: string; value: boolean }) =>
      tenantWorkspaceApi.setRetailFavorite(id, value),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["retail"] }),
  });
  const productInteraction = useMutation({
    mutationFn: (interactionKey: string) =>
      tenantWorkspaceApi.recordProductInteraction({
        productId: interactionTarget!.id,
        interactionKey,
        mobile: interactionMobile.trim() || null,
        customerName: interactionCustomerName.trim() || null,
      }),
    onSuccess: async () => {
      setInteractionTarget(null);
      setInteractionMobile("");
      setInteractionCustomerName("");
      await queryClient.invalidateQueries({ queryKey: ["retail", "product-interactions"] });
      await queryClient.invalidateQueries({ queryKey: ["retail", "recent"] });
      await queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
  const updateManagedInvoice = async (
    invoice: RetailInvoice,
  ): Promise<void> => {
    setManageInvoice(invoice);
    await refreshAfterAction();
  };
  const addPayment = useMutation({
    mutationFn: () =>
      tenantWorkspaceApi.addRetailInvoicePayment(
        manageInvoice!.id ?? manageInvoice!.invoiceId!,
        {
          paymentMethod,
          amount: paymentAmount,
          reference: paymentReference || null,
        },
      ),
    onSuccess: updateManagedInvoice,
  });
  const returnSale = useMutation({
    mutationFn: () =>
      tenantWorkspaceApi.returnRetailInvoice(
        manageInvoice!.id ?? manageInvoice!.invoiceId!,
        {
          items: Object.entries(returnQuantities)
            .filter(([, quantity]) => quantity > 0)
            .map(([invoiceItemId, quantity]) => ({ invoiceItemId, quantity })),
          refundMethod: returnMethod,
          reason: returnReason || null,
        },
      ),
    onSuccess: updateManagedInvoice,
  });
  const cancelSale = useMutation({
    mutationFn: () =>
      tenantWorkspaceApi.cancelRetailInvoice(
        manageInvoice!.id ?? manageInvoice!.invoiceId!,
      ),
    onSuccess: updateManagedInvoice,
  });
  const isPending =
    introduction.isPending ||
    sale.isPending ||
    addPayment.isPending ||
    returnSale.isPending ||
    cancelSale.isPending ||
    busy;

  const choose = (product: RetailProduct): void => {
    setIntroductionResult(null);
    setSaleResult(null);
    if (mode === "sale" && product.variants.length > 0) {
      setVariantPicker(product);
      return;
    }
    const key = product.id;
    if (selectedOrder.has(key)) {
      removeSelectedLine(key);
      return;
    }
    addSelectedLine({ key, product });
  };
  const addSelectedLine = (line: SelectedRetailLine): void => {
    if (selectedLines.length >= maxSelectedProducts) {
      setSelectionMessage("حداکثر ۲۰ کالا یا خدمت را می‌توانید انتخاب کنید.");
      return;
    }
    if (selectedOrder.has(line.key)) {
      setSelectionMessage("این مورد قبلاً انتخاب شده است.");
      return;
    }
    setSelectedLines((current) => [...current, line]);
    setQuantities((current) => ({ ...current, [line.key]: 1 }));
    setSelectionMessage(null);
  };
  const removeSelectedLine = (key: string): void => {
    setSelectedLines((current) => current.filter((line) => line.key !== key));
    setQuantities((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  };
  const changeQuantity = (line: SelectedRetailLine, delta: number): void =>
    setQuantities((current) => {
      const stock = selectedBranch(line)?.onHandQuantity;
      const maximum =
        stock === undefined ? 999 : Math.max(1, Math.min(999, stock));
      return {
        ...current,
        [line.key]: Math.min(
          maximum,
          Math.max(1, (current[line.key] ?? 1) + delta),
        ),
      };
    });
  const clearSelection = (): void => {
    setSelectedLines([]);
    setQuantities({});
    setSelectionMessage(null);
    setMobileCheckoutOpen(false);
  };
  const switchMode = (next: Mode): void => {
    setMode(next);
    setIntroductionResult(null);
    setSaleResult(null);
  };
  const requestExit = (): void => {
    if (selectedLines.length > 0 || mobileCheckoutOpen) {
      setMobileCheckoutOpen(false);
      setExitWarningOpen(true);
      return;
    }
    navigate("/");
  };
  const exitDesk = (): void => {
    setExitWarningOpen(false);
    navigate("/");
  };
  const openInvoice = (invoice: RetailInvoice): void => {
    setManageInvoice(invoice);
    setPaymentAmount(invoice.balanceDue ?? 0);
    setPaymentReference("");
    setReturnQuantities({});
    setReturnMethod(
      invoice.paymentMethod === "Multiple" ? "Card" : invoice.paymentMethod,
    );
    setReturnReason("");
  };
  const selectedSummary = `${number(selectedLines.length)} قلم انتخاب شده`;
  const saleSelectionReady = selectedLines.every(
    (line) =>
      !line.product.variants.length ||
      Boolean(
        line.variantId &&
        line.branchId &&
        (selectedBranch(line)?.onHandQuantity ?? 0) >=
          (quantities[line.key] ?? 1),
      ),
  );
  const selectedProductCount = (productId: string): number =>
    selectedLines.filter((line) => line.product.id === productId).length;
  const metrics = dashboardQuery.data;

  return (
    <div className="retail-mobile-sheet fixed inset-0 z-[9000] space-y-4 overflow-y-auto overscroll-contain bg-slate-50 px-3 pb-32 dark:bg-slate-950 xl:static xl:z-auto xl:space-y-5 xl:overflow-visible xl:bg-transparent xl:px-0 xl:pb-36 xl:dark:bg-transparent">
      <header className="sticky top-0 z-40 -mx-3 border-b border-slate-200/80 bg-white/95 px-3 pb-3 pt-[max(env(safe-area-inset-top),0.75rem)] shadow-sm backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/95 xl:hidden">
        <div className="flex items-center gap-2">
          <button
            aria-label="خروج از میز سریع"
            className="grid size-11 shrink-0 place-items-center rounded-2xl border border-slate-200 bg-white text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
            onClick={requestExit}
            type="button"
          >
            <AppIcon name="close" size={19} />
          </button>
          <div className="grid min-w-0 flex-1 grid-cols-2 gap-1 rounded-2xl bg-slate-100 p-1 dark:bg-slate-900">
            {(
              [
                ["introduce", "معرفی سریع", "send"],
                ["sale", "فروش سریع", "check"],
              ] as const
            ).map(([key, title, icon]) => (
              <button
                aria-pressed={mode === key}
                className={`flex min-h-10 min-w-0 items-center justify-center gap-1.5 rounded-xl px-2 text-[11px] font-black transition ${mode === key ? "bg-white text-slate-950 shadow-sm dark:bg-slate-800 dark:text-white" : "text-slate-500 dark:text-slate-400"}`}
                key={key}
                onClick={() => switchMode(key)}
                type="button"
              >
                <AppIcon
                  className={
                    mode === key
                      ? key === "sale"
                        ? "text-emerald-600"
                        : "text-brand-600"
                      : ""
                  }
                  name={icon}
                  size={16}
                />
                <span className="truncate">{title}</span>
              </button>
            ))}
          </div>
        </div>
        <label className="relative mt-3 block">
          <AppIcon
            className="absolute right-4 top-3.5 text-slate-400"
            name="search"
            size={20}
          />
          <input
            autoComplete="off"
            className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3.5 pl-4 pr-11 text-sm font-bold text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-brand-400 focus:bg-white focus:ring-4 focus:ring-brand-50 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:border-brand-500 dark:focus:bg-slate-900 dark:focus:ring-brand-950/50"
            data-guide-target="retail-product-search-mobile"
            dir="auto"
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && products.length === 1)
                choose(products[0]);
            }}
            placeholder="جست‌وجوی نام، SKU یا بارکد"
            value={search}
          />
        </label>
      </header>

      <section className="hidden overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm sm:p-6 dark:border-slate-800 dark:bg-slate-900 xl:block">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-black text-brand-600">ویترین هوشمند فروشنده</p>
            <h1 className="mt-1 text-xl font-black text-ink-950 sm:text-3xl">
              انتخاب کن، معرفی کن یا بفروش
            </h1>
            <p className="mt-2 hidden max-w-2xl text-sm leading-7 text-slate-500 sm:block">
              از ویترین کالاها چند مورد را لمس کنید؛ شماره مشتری و ثبت نهایی در یک مرحله انجام می‌شود.
            </p>
          </div>
          <div className="grid w-full grid-cols-2 gap-2 rounded-2xl bg-slate-100 p-1.5 lg:w-auto lg:min-w-[430px]">
            {(
              [
                ["introduce", "معرفی سریع", "لندینگ و پیامک", "send"],
                ["sale", "فروش One‑Click", "فاکتور و پیام تشکر", "check"],
              ] as const
            ).map(([key, title, subtitle, icon]) => (
              <button
                className={`flex min-w-0 items-center gap-2 rounded-xl p-2.5 text-right transition sm:gap-3 sm:p-3 ${mode === key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:bg-white/70"}`}
                key={key}
                onClick={() => switchMode(key)}
                type="button"
              >
                <span
                  className={`grid size-9 shrink-0 place-items-center rounded-xl sm:size-10 ${mode === key ? key === "sale" ? "bg-emerald-50 text-emerald-600" : "bg-brand-50 text-brand-600" : "bg-white text-slate-400"}`}
                >
                  <AppIcon name={icon} size={18} />
                </span>
                <span>
                  <strong className="block truncate text-xs sm:text-sm">{title}</strong>
                  <small
                    className="mt-0.5 hidden truncate text-slate-400 sm:block"
                  >
                    {subtitle}
                  </small>
                </span>
              </button>
            ))}
          </div>
        </div>
        <label className="relative mt-4 block">
          <AppIcon
            className="absolute right-4 top-4 text-slate-400"
            name="search"
            size={22}
          />
          <input
            autoComplete="off"
            className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-12 py-4 text-sm font-bold text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-brand-400 focus:bg-white focus:ring-4 focus:ring-brand-50 sm:text-base"
            data-guide-target="retail-product-search"
            dir="auto"
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && products.length === 1)
                choose(products[0]);
            }}
            placeholder="نام کالا یا خدمت، SKU یا بارکد"
            ref={searchInput}
            value={search}
          />
          <span className="absolute left-4 top-4 hidden text-xs font-bold text-slate-400 sm:block">
            Enter برای انتخاب
          </span>
        </label>
      </section>

      <section className="rounded-[1.5rem] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:rounded-[2rem] sm:p-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div><p className="text-xs font-bold text-brand-600">دسترسی سریع</p><h2 className="mt-1 text-lg font-black text-ink-950 dark:text-white">دسته‌بندی‌ها</h2></div>
          <span className="rounded-full bg-slate-100 px-3 py-1.5 text-[11px] font-bold text-slate-500 dark:bg-slate-800 dark:text-slate-300">{number(products.length)} مورد</span>
        </div>
        <div className="-mx-1 flex snap-x gap-3 overflow-x-auto px-1 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {[
            { key: "all", title: "همه", symbol: "همه" },
            { key: "favorites", title: "محبوب‌ها", symbol: "★" },
            { key: "recent", title: "اخیراً", symbol: "↺" },
            ...catalogShelves.map((item) => ({
              ...item,
              symbol: item.title.trim().slice(0, 1) || "•",
            })),
          ].map((shelf, index) => (
            <button className="w-[76px] shrink-0 snap-start text-center sm:w-[92px]" key={shelf.key} onClick={() => { setActiveShelf(shelf.key); setSearch(""); }} type="button">
              <span className={`relative mx-auto grid size-[68px] place-items-center overflow-hidden rounded-[1.55rem] bg-gradient-to-br text-base font-black transition sm:size-20 ${shelfTones[index % shelfTones.length]} ${activeShelf === shelf.key && !stableSearch ? "ring-2 ring-brand-500 ring-offset-2 dark:ring-offset-slate-900" : ""}`}>
                <span>{shelf.symbol}</span>
                {"imageUrl" in shelf && shelf.imageUrl ? (
                  <img
                    alt=""
                    className="absolute inset-0 size-full object-cover"
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                    src={shelf.imageUrl}
                  />
                ) : null}
              </span>
              <span className={`mt-2 block truncate text-[11px] font-black sm:text-xs ${activeShelf === shelf.key && !stableSearch ? "text-brand-700 dark:text-brand-300" : "text-slate-600 dark:text-slate-300"}`}>{shelf.title}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="hidden grid-cols-2 gap-3 xl:grid xl:grid-cols-5">
        {[
          ["مشتری امروز", metrics?.todayCustomers ?? 0],
          ["معرفی امروز", metrics?.todayLandings ?? 0],
          ["بازدید امروز", metrics?.todayOpened ?? 0],
          ["مشاهده کالا", metrics?.todayProductViews ?? 0],
          ["خرید ثبت‌شده", metrics?.todayPurchases ?? 0],
        ].map(([label, value]) => (
          <article
            className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
            key={String(label)}
          >
            <p className="text-xs font-bold text-slate-400">{label}</p>
            <p className="mt-2 text-2xl font-black text-ink-950">
              {number(Number(value))}
            </p>
          </article>
        ))}
      </section>

      {productInteractionEnabled ? (
        <section className="hidden rounded-3xl border border-violet-100 bg-violet-50/50 p-4 shadow-sm dark:border-violet-900 dark:bg-violet-950/30 sm:p-5 xl:block">
          <div className="flex flex-wrap items-start justify-between gap-3"><div><p className="text-sm font-black text-violet-700">نشانه‌های علاقه حضوری</p><h2 className="mt-1 text-lg font-black text-ink-950">کالاها و خدماتی که بیشتر درباره‌شان صحبت شده</h2><p className="mt-1 text-xs leading-6 text-slate-500">آمار ۳۰ روز اخیر؛ ثبت بدون شماره موبایل نیز در شمارش لحاظ می‌شود.</p></div><span className="rounded-xl bg-white px-3 py-2 text-xs font-black text-violet-700">امروز: {number(interactionSummaryQuery.data?.todayCount ?? 0)} تعامل</span></div>
          {interactionSummaryQuery.isLoading ? <p className="mt-4 text-sm font-bold text-slate-400">در حال دریافت تحلیل تعامل‌ها…</p> : null}
          {interactionSummaryQuery.isError ? <p className="mt-4 rounded-xl bg-rose-50 p-3 text-xs font-bold text-rose-700">تحلیل تعامل‌ها دریافت نشد: {getErrorMessage(interactionSummaryQuery.error)}</p> : null}
          {!interactionSummaryQuery.isLoading && !interactionSummaryQuery.isError && !interactionSummaryQuery.data?.topProducts.length ? <p className="mt-4 rounded-2xl border border-dashed border-violet-200 bg-white/70 p-4 text-sm text-slate-500">هنوز تعاملی ثبت نشده است. روی «ثبت تعامل» در کارت هر کالا بزنید.</p> : null}
          {interactionSummaryQuery.data?.topProducts.length ? <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-5">{interactionSummaryQuery.data.topProducts.map((product, index) => <article className="rounded-2xl bg-white p-3 shadow-sm" key={product.productId}><div className="flex items-start justify-between gap-2"><p className="line-clamp-2 text-sm font-black text-slate-800">{product.productName}</p><span className="grid size-6 shrink-0 place-items-center rounded-full bg-violet-100 text-[10px] font-black text-violet-700">{number(index + 1)}</span></div><p className="mt-2 text-lg font-black text-violet-700">{number(product.totalCount)} <span className="text-[10px]">تعامل</span></p><div className="mt-2 flex flex-wrap gap-1">{product.options.slice(0, 3).map((option) => <span className="rounded-md bg-slate-100 px-1.5 py-1 text-[10px] font-bold text-slate-600" key={option.interactionKey}>{option.interactionTitle}: {number(option.count)}</span>)}</div>{product.anonymousCount ? <p className="mt-2 text-[10px] text-slate-400">{number(product.anonymousCount)} ثبت بدون شماره مشتری</p> : null}</article>)}</div> : null}
        </section>
      ) : null}

      <section className="grid gap-5 xl:grid-cols-[1fr_380px]">
        <div className="rounded-[1.5rem] border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:rounded-[2rem] sm:p-5">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h2 className="font-black text-ink-950 dark:text-white">
                {stableSearch
                  ? "نتیجه جست‌وجو"
                  : activeShelf === "favorites"
                    ? "محبوب‌ها"
                    : activeShelf === "recent"
                      ? "کالاهای اخیر"
                      : catalogShelves.find((item) => item.key === activeShelf)?.title ?? "همه کالاها و خدمات"}
              </h2>
              <p className="mt-1 text-xs text-slate-400">
                برای افزودن یا حذف، روی کارت بزنید.
              </p>
            </div>
            <span className="shrink-0 rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-500 dark:bg-slate-800 dark:text-slate-300">
              {number(displayedProducts.length)} مورد
            </span>
          </div>
          {productsQuery.isLoading ? (
            <p className="p-8 text-center text-sm font-bold text-slate-400">
              در حال جست‌وجو…
            </p>
          ) : null}
          {productsQuery.isError ? (
            <p className="rounded-xl bg-rose-50 p-3 text-sm font-bold text-rose-700">
              {getErrorMessage(productsQuery.error)}
            </p>
          ) : null}
          {selectionMessage ? (
            <p className="mb-3 rounded-xl bg-amber-50 px-3 py-2.5 text-xs font-bold text-amber-800">
              {selectionMessage}
            </p>
          ) : null}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {displayedProducts.map((product) => {
              const count = selectedProductCount(product.id);
              return (
                <button
                  aria-pressed={count > 0}
                  className={`group relative min-w-0 overflow-hidden rounded-[1.45rem] border bg-white p-2 text-right transition active:scale-[0.985] dark:bg-slate-950 sm:p-3 ${count > 0 ? "border-brand-500 ring-2 ring-brand-100 dark:ring-brand-950" : "border-slate-200 hover:border-brand-300 hover:shadow-md dark:border-slate-800 dark:hover:border-brand-700"}`}
                  key={product.id}
                  onClick={() => choose(product)}
                  type="button"
                >
                  {count > 0 ? (
                    <span className="absolute left-3 top-3 z-10 grid min-w-7 place-items-center rounded-full bg-brand-600 px-1.5 py-1 text-[11px] font-black text-white shadow-lg">
                      ✓ {number(count)}
                    </span>
                  ) : null}
                  <div className="relative aspect-square w-full overflow-hidden rounded-[1.15rem] bg-gradient-to-br from-slate-100 to-slate-50 text-brand-600 dark:from-slate-800 dark:to-slate-900 dark:text-brand-300">
                      <span className="absolute inset-0 grid place-items-center">
                        <AppIcon name="box" size={34} />
                      </span>
                      {product.imageUrl ? (
                        <img
                          alt={product.name}
                          className="absolute inset-0 size-full object-cover transition duration-300 group-hover:scale-105"
                          loading="lazy"
                          onError={(event) => {
                            event.currentTarget.style.display = "none";
                          }}
                          src={product.imageUrl}
                        />
                      ) : null}
                      <span className={`absolute bottom-2 right-2 rounded-full px-2 py-1 text-[9px] font-black shadow-sm ${product.availabilityStatus === "Available" ? "bg-emerald-500 text-white" : product.availabilityStatus === "OnRequest" ? "bg-amber-400 text-amber-950" : "bg-slate-700 text-white"}`}>{availabilityLabel[product.availabilityStatus]}</span>
                      <span
                          className="absolute right-2 top-2 grid size-8 place-items-center rounded-xl bg-white/95 text-lg text-amber-500 shadow-sm backdrop-blur"
                          onClick={(event) => {
                            event.stopPropagation();
                            favorite.mutate({
                              id: product.id,
                              value: !product.isFavorite,
                            });
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          {product.isFavorite ? "★" : "☆"}
                        </span>
                  </div>
                    <div className="min-w-0 px-1 pb-1 pt-3">
                      <p className="truncate text-[10px] font-bold text-slate-400">{product.categoryName ?? itemTypeLabel(product.itemType)}{product.brand ? ` · ${product.brand}` : ""}</p>
                      <h3 className="mt-1 line-clamp-2 min-h-10 text-xs font-black leading-5 text-slate-800 dark:text-slate-100 sm:text-sm">{product.name}</h3>
                      <div className="mt-2 flex min-w-0 items-end justify-between gap-2">
                        <div className="min-w-0"><p className="truncate text-[11px] font-black text-brand-700 sm:text-xs">{money(product.price, product.currency)}</p>{product.previousPrice && product.price && product.previousPrice > product.price ? <p className="mt-0.5 truncate text-[9px] text-slate-400 line-through">{money(product.previousPrice, product.currency)}</p> : null}</div>
                        <span className={`grid size-8 shrink-0 place-items-center rounded-xl text-base font-black ${count > 0 ? "bg-brand-600 text-white" : "bg-slate-100 text-brand-700 dark:bg-slate-800 dark:text-brand-300"}`}>{count > 0 ? "−" : "+"}</span>
                      </div>
                      {product.variants.length > 0 ? (
                        <p className="mt-2 truncate text-[9px] font-bold text-violet-600 sm:text-[10px]">
                          {number(product.variants.length)} رنگ/سایز · موجودی شعبه
                        </p>
                      ) : null}
                  {productInteractionEnabled && interactionSettingsQuery.data?.options.some((option) => option.isActive) ? (
                    <span
                      className="relative z-10 mt-2 inline-flex w-full items-center justify-center gap-1 rounded-xl bg-violet-50 px-2 py-2 text-[10px] font-black text-violet-700 hover:bg-violet-100"
                      onClick={(event) => {
                        event.stopPropagation();
                        setInteractionTarget(product);
                        setInteractionMobile("");
                        setInteractionCustomerName("");
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          event.stopPropagation();
                          setInteractionTarget(product);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <AppIcon name="activity" size={14} />
                      ثبت تعامل
                    </span>
                  ) : null}
                    </div>
                </button>
              );
            })}
          </div>
          {!productsQuery.isLoading && displayedProducts.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 p-10 text-center">
              <AppIcon
                className="mx-auto text-slate-300"
                name="box"
                size={28}
              />
              <p className="mt-3 text-sm font-bold text-slate-500">
                ابتدا کالا یا خدمت را در کاتالوگ ثبت کنید.
              </p>
            </div>
          ) : null}
        </div>

        {mobileCheckoutOpen ? (
          <button aria-label="بستن مرحله ثبت نهایی" className="fixed inset-0 z-[10000] bg-slate-950/45 backdrop-blur-[1px] xl:hidden" onClick={() => setMobileCheckoutOpen(false)} type="button" />
        ) : null}
        <aside ref={customerPanelRef} className={`${mobileCheckoutOpen ? "fixed inset-x-0 bottom-0 z-[10010] max-h-[91dvh] overflow-y-auto rounded-t-[2rem] px-4 pb-[max(env(safe-area-inset-bottom),1.25rem)] pt-4 shadow-2xl" : "hidden"} self-start border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 xl:sticky xl:inset-auto xl:top-5 xl:block xl:max-h-none xl:overflow-visible xl:rounded-3xl xl:p-5 xl:shadow-sm`}>
          <div className="mb-3 flex items-center justify-center xl:hidden"><span className="h-1.5 w-12 rounded-full bg-slate-200" /></div>
          <div className="flex items-center justify-between">
            <div>
              <p
                className={`text-sm font-bold ${mode === "sale" ? "text-emerald-600" : "text-brand-600"}`}
              >
                {mode === "sale" ? "ثبت خرید قطعی" : "ارسال معرفی"}
              </p>
              <h2 className="mt-1 text-xl font-black text-ink-950 dark:text-white">
                {mode === "sale" ? "فاکتور One‑Click" : "ارسال برای مشتری"}
              </h2>
            </div>
            <div className="flex items-center gap-2"><button aria-label="بستن" className="grid size-10 place-items-center rounded-xl bg-slate-100 text-slate-500 xl:hidden" onClick={() => setMobileCheckoutOpen(false)} type="button"><AppIcon name="close" size={18} /></button><span className={`grid size-11 place-items-center rounded-2xl ${mode === "sale" ? "bg-emerald-50 text-emerald-600" : "bg-brand-50 text-brand-600"}`}><AppIcon name={mode === "sale" ? "check" : "send"} size={20} /></span></div>
          </div>
          {selectedLines.length ? (
            <div className="mt-4 rounded-2xl bg-slate-50 p-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-black text-slate-600">
                  {selectedSummary}
                </p>
                <button
                  className="text-[11px] font-bold text-slate-400 underline"
                  onClick={clearSelection}
                  type="button"
                >
                  حذف همه
                </button>
              </div>
              <ol className="mt-3 max-h-64 space-y-2 overflow-y-auto">
                {selectedLines.map((line, index) => {
                  const variant = selectedVariant(line);
                  const branch = selectedBranch(line);
                  const quantity = quantities[line.key] ?? 1;
                  return (
                    <li
                      className="flex items-center gap-2 rounded-xl bg-white p-2.5"
                      key={line.key}
                    >
                      <span className="grid size-6 shrink-0 place-items-center rounded-full bg-brand-100 text-[10px] font-black text-brand-700">
                        {number(index + 1)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-black">
                          {line.product.name}
                        </p>
                        <p className="mt-0.5 truncate text-[10px] text-slate-400">
                          {variant
                            ? `${variant.name} · ${branch?.branchName ?? "بدون شعبه"} · `
                            : ""}
                          {money(linePrice(line), line.product.currency)}
                          {variant
                            ? ` · موجودی ${number(branch?.onHandQuantity ?? 0)}`
                            : ""}
                        </p>
                      </div>
                      {mode === "sale" ? (
                        <div className="flex shrink-0 items-center rounded-lg border border-slate-200">
                          <button
                            className="grid size-7 place-items-center text-brand-600 disabled:opacity-30"
                            disabled={
                              branch ? quantity >= branch.onHandQuantity : false
                            }
                            onClick={() => changeQuantity(line, 1)}
                            type="button"
                          >
                            +
                          </button>
                          <span className="min-w-7 text-center text-xs font-black">
                            {number(quantity)}
                          </span>
                          <button
                            className="grid size-7 place-items-center text-slate-500"
                            onClick={() => changeQuantity(line, -1)}
                            type="button"
                          >
                            −
                          </button>
                        </div>
                      ) : null}
                      <button
                        className="grid size-7 shrink-0 place-items-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                        onClick={() => removeSelectedLine(line.key)}
                        type="button"
                      >
                        <AppIcon name="close" size={14} />
                      </button>
                    </li>
                  );
                })}
              </ol>
            </div>
          ) : (
            <div className="mt-4 rounded-2xl border border-dashed border-slate-200 p-5 text-center text-sm font-bold text-slate-400">
              حداقل یک کالا یا خدمت انتخاب کنید.
            </div>
          )}

          <div className="mt-4 space-y-3">
            <label>
              <span className="mb-1.5 block text-xs font-bold text-slate-500">
                {mode === "introduce" ? "شماره موبایل مشتری *" : "شماره موبایل مشتری"}
                {mode === "sale" ? <span className="mr-1 font-normal text-slate-400">(پیشنهادی)</span> : null}
              </span>
              <input
                ref={customerMobileInputRef}
                className="input font-sans"
                data-guide-target="retail-customer-mobile"
                dir="ltr"
                disabled={!selectedLines.length || isPending}
                inputMode="tel"
                onChange={(event) => setMobile(event.target.value)}
                placeholder="0912…"
                value={mobile}
              />
              {mode === "sale" ? <span className="mt-1.5 block text-[11px] leading-5 text-slate-400">بدون شماره هم می‌توانید فروش را ثبت کنید؛ قبل از ثبت نهایی یک هشدار تأیید نمایش داده می‌شود.</span> : null}
            </label>
            <label>
              <span className="mb-1.5 block text-xs font-bold text-slate-500">
                نام مشتری <span className="font-normal">(اختیاری)</span>
              </span>
              <input
                className="input"
                disabled={!selectedLines.length || isPending}
                onChange={(event) => setCustomerName(event.target.value)}
                placeholder="در صورت مشتری جدید"
                value={customerName}
              />
            </label>
            {mode === "introduce" ? (
              <label>
                <span className="mb-1.5 block text-xs font-bold text-slate-500">
                  اعتبار لینک اختصاصی
                </span>
                <select
                  className="input"
                  disabled={!selectedLines.length || isPending}
                  onChange={(event) =>
                    setLandingValidityDays(Number(event.target.value))
                  }
                  value={landingValidityDays}
                >
                  <option value={1}>۱ روز</option>
                  <option value={3}>۳ روز</option>
                  <option value={7}>۷ روز (پیشنهادی)</option>
                  <option value={30}>۳۰ روز</option>
                  <option value={0}>بدون انقضا</option>
                </select>
              </label>
            ) : (
              <>
                <label>
                  <span className="mb-1.5 block text-xs font-bold text-slate-500">
                    روش پرداخت
                  </span>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {selectablePaymentMethods.map((key) => (
                      <button
                        className={`rounded-xl border px-2 py-2.5 text-xs font-black ${paymentMethod === key ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-500"}`}
                        key={key}
                        onClick={() => setPaymentMethod(key)}
                        type="button"
                      >
                        {paymentLabels[key]}
                      </button>
                    ))}
                  </div>
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-bold text-slate-500">
                    وضعیت پرداخت در لحظه فروش
                  </span>
                  <div className="grid grid-cols-3 gap-2">
                    {(
                      [
                        ["Paid", "کامل"],
                        ["PartiallyPaid", "ناقص"],
                        ["Unpaid", "بعداً"],
                      ] as const
                    ).map(([key, label]) => (
                      <button
                        className={`rounded-xl border px-2 py-2.5 text-xs font-black ${paymentStatus === key ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-500"}`}
                        key={key}
                        onClick={() => {
                          setPaymentStatus(key);
                          if (key === "Paid") setPaidAmount(total);
                          if (key === "Unpaid") setPaidAmount(0);
                        }}
                        type="button"
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </label>
                {paymentStatus === "PartiallyPaid" ? (
                  <label>
                    <span className="mb-1.5 block text-xs font-bold text-slate-500">
                      مبلغ دریافت‌شده اکنون
                    </span>
                    <input
                      className="input font-sans"
                      dir="ltr"
                      disabled={!selectedLines.length || isPending}
                      inputMode="numeric"
                      min={0}
                      onChange={(event) =>
                        setPaidAmount(
                          Math.max(0, Number(event.target.value) || 0),
                        )
                      }
                      value={paidAmount || ""}
                    />
                  </label>
                ) : null}
                <div className="overflow-hidden rounded-2xl border border-violet-100 bg-violet-50/50 dark:border-violet-900/60 dark:bg-violet-950/20">
                  <button
                    aria-expanded={discountsOpen}
                    className="flex w-full touch-manipulation items-center justify-between gap-3 px-3.5 py-3 text-right transition hover:bg-violet-100/60 active:bg-violet-100 dark:hover:bg-violet-900/30 dark:active:bg-violet-900/40"
                    onClick={() => setDiscountsOpen((value) => !value)}
                    type="button"
                  >
                    <span className="flex min-w-0 items-center gap-2.5">
                      <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-200">
                        <AppIcon name="sparkles" size={17} />
                      </span>
                      <span className="min-w-0">
                        <strong className="block text-sm font-black text-violet-950 dark:text-violet-100">
                          تخفیفات و اعتبار
                        </strong>
                        <span className="mt-0.5 block text-[11px] text-violet-600 dark:text-violet-300">
                          {activeDiscountOptions > 0
                            ? `${number(activeDiscountOptions)} مورد وارد شده`
                            : "اختیاری — برای باز کردن لمس کنید"}
                        </span>
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      {safeDiscount > 0 ? (
                        <span className="hidden text-xs font-black text-emerald-700 sm:inline dark:text-emerald-300">
                          − {money(safeDiscount)}
                        </span>
                      ) : null}
                      <span
                        className={`text-violet-500 transition-transform ${discountsOpen ? "-rotate-90" : "rotate-90"}`}
                      >
                        <AppIcon name="chevron-left" size={18} />
                      </span>
                    </span>
                  </button>
                  {discountsOpen ? (
                    <div className="space-y-3 border-t border-violet-100 p-3 dark:border-violet-900/60">
                      <label>
                        <span className="mb-1.5 block text-xs font-bold text-violet-800 dark:text-violet-200">
                          تخفیف کل فاکتور
                        </span>
                        <input
                          className="input font-sans"
                          dir="ltr"
                          disabled={!selectedLines.length || isPending}
                          inputMode="numeric"
                          min={0}
                          onChange={(event) =>
                            setDiscountAmount(
                              Math.max(0, Number(event.target.value) || 0),
                            )
                          }
                          value={discountAmount || ""}
                        />
                      </label>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <label>
                          <span className="mb-1.5 block text-xs font-bold text-violet-800 dark:text-violet-200">
                            کد تخفیف <span className="font-normal">(اختیاری)</span>
                          </span>
                          <input
                            className="input font-sans"
                            dir="ltr"
                            disabled={!selectedLines.length || isPending}
                            onChange={(event) => setDiscountCode(event.target.value)}
                            placeholder="WELCOME10"
                            value={discountCode}
                          />
                        </label>
                        <label>
                          <span className="mb-1.5 block text-xs font-bold text-violet-800 dark:text-violet-200">
                            امتیاز قابل مصرف
                          </span>
                          <input
                            className="input font-sans"
                            dir="ltr"
                            disabled={!selectedLines.length || isPending}
                            inputMode="numeric"
                            min={0}
                            onChange={(event) =>
                              setPointsToRedeem(
                                Math.max(0, Number(event.target.value) || 0),
                              )
                            }
                            placeholder="۰"
                            value={pointsToRedeem || ""}
                          />
                        </label>
                        <label>
                          <span className="mb-1.5 block text-xs font-bold text-violet-800 dark:text-violet-200">
                            کد کارت هدیه{" "}
                            <span className="font-normal">(اختیاری)</span>
                          </span>
                          <input
                            className="input font-sans"
                            dir="ltr"
                            disabled={!selectedLines.length || isPending}
                            onChange={(event) => setGiftCardCode(event.target.value)}
                            placeholder="GIFT-2026"
                            value={giftCardCode}
                          />
                        </label>
                        <label>
                          <span className="mb-1.5 block text-xs font-bold text-violet-800 dark:text-violet-200">
                            مبلغ استفاده از کارت
                          </span>
                          <input
                            className="input font-sans"
                            dir="ltr"
                            disabled={
                              !selectedLines.length ||
                              isPending ||
                              !giftCardCode.trim()
                            }
                            inputMode="numeric"
                            min={0}
                            onChange={(event) =>
                              setGiftCardAmount(
                                Math.max(0, Number(event.target.value) || 0),
                              )
                            }
                            placeholder="خالی = تا سقف اعتبار"
                            value={giftCardAmount || ""}
                          />
                        </label>
                      </div>
                      <p className="text-[11px] leading-5 text-violet-700 dark:text-violet-300">
                        مبلغ نهایی، اعتبار کارت و شرایط کد تخفیف در لحظه ثبت فروش
                        توسط سیستم کنترل می‌شود.
                      </p>
                    </div>
                  ) : null}
                </div>
                <div className="rounded-2xl bg-slate-950 p-4 text-white">
                  <div className="flex justify-between text-xs text-slate-400">
                    <span>جمع اقلام</span>
                    <span>{money(subtotal)}</span>
                  </div>
                  {safeDiscount > 0 ? (
                    <div className="mt-2 flex justify-between text-xs text-emerald-300">
                      <span>تخفیف</span>
                      <span>− {money(safeDiscount)}</span>
                    </div>
                  ) : null}
                  <div className="mt-3 flex justify-between border-t border-white/10 pt-3 text-base font-black">
                    <span>قابل پرداخت</span>
                    <span>{money(total)}</span>
                  </div>
                  {paymentStatus !== "Paid" ? (
                    <div className="mt-2 flex justify-between text-xs text-amber-300">
                      <span>مانده پس از ثبت</span>
                      <span>
                        {money(
                          Math.max(
                            total -
                              (paymentStatus === "PartiallyPaid"
                                ? paidAmount
                                : 0),
                            0,
                          ),
                        )}
                      </span>
                    </div>
                  ) : null}
                </div>
              </>
            )}
            {mode === "introduce" ? (
              <button
                data-guide-target="retail-introduce-submit"
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-brand-600 py-3.5 text-sm font-black text-white disabled:opacity-50"
                disabled={!selectedLines.length || !mobile.trim() || isPending}
                onClick={() => introduction.mutate()}
                type="button"
              >
                <AppIcon name="send" size={18} />
                {introduction.isPending
                  ? "در حال ارسال…"
                  : `معرفی ${number(selectedLines.length)} مورد`}
              </button>
            ) : (
              <button
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-4 text-base font-black text-white shadow-lg shadow-emerald-500/20 disabled:opacity-50"
                disabled={
                  !selectedLines.length ||
                  !saleSelectionReady ||
                  isPending
                }
                onClick={submitSale}
                type="button"
              >
                <AppIcon name="check" size={19} />
                {sale.isPending ? "در حال ثبت فاکتور…" : "ثبت فروش با یک کلیک"}
              </button>
            )}
            {mode === "sale" && selectedLines.length && !saleSelectionReady ? (
              <p className="rounded-xl bg-amber-50 p-3 text-xs font-bold leading-6 text-amber-800">
                برای کالای دارای واریانت، انتخاب شعبه و موجودی کافی الزامی است.
              </p>
            ) : null}
          </div>
          {introduction.isError || sale.isError ? (
            <p className="mt-3 rounded-xl bg-rose-50 p-3 text-xs font-bold leading-6 text-rose-700">
              {getErrorMessage(introduction.error ?? sale.error)}
            </p>
          ) : null}
          {introductionResult ? (
            <div
              className={`mt-4 rounded-2xl p-4 text-sm ${introductionResult.error ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-800"}`}
            >
              <p className="font-black">
                صفحه معرفی ساخته شد
                {introductionResult.error
                  ? "؛ پیامک نیاز به بررسی دارد"
                  : " و پیامک ارسال شد"}
              </p>
              <a
                className="mt-2 block break-all font-sans text-xs underline"
                href={introductionResult.url}
                rel="noreferrer"
                target="_blank"
              >
                مشاهده صفحه مشتری
              </a>
              {introductionResult.error ? (
                <p className="mt-2 text-xs">{introductionResult.error}</p>
              ) : null}
            </div>
          ) : null}
          {saleResult ? (
            <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-emerald-800">
              <div className="flex items-center gap-2">
                <AppIcon name="check" size={18} />
                <p className="font-black">فاکتور با موفقیت ثبت شد</p>
              </div>
              <p className="mt-2 font-sans text-xs">
                {saleResult.invoiceNumber}
              </p>
              <p className="mt-2 text-lg font-black">
                {money(saleResult.totalAmount, saleResult.currency)}
              </p>
              {saleResult.discountCodeAmount ||
              saleResult.pointsDiscountAmount ||
              saleResult.giftCardAmount ||
              saleResult.earnedPoints ? (
                <div className="mt-3 grid gap-2 rounded-xl bg-white/70 p-3 text-xs font-bold text-emerald-900 sm:grid-cols-2">
                  {saleResult.discountCodeAmount ? (
                    <p>
                      کد تخفیف: −{" "}
                      {money(
                        saleResult.discountCodeAmount,
                        saleResult.currency,
                      )}
                    </p>
                  ) : null}
                  {saleResult.pointsDiscountAmount ? (
                    <p>
                      تخفیف امتیاز: −{" "}
                      {money(
                        saleResult.pointsDiscountAmount,
                        saleResult.currency,
                      )}
                    </p>
                  ) : null}
                  {saleResult.giftCardAmount ? (
                    <p>
                      کارت هدیه: −{" "}
                      {money(saleResult.giftCardAmount, saleResult.currency)}
                    </p>
                  ) : null}
                  {saleResult.earnedPoints ? (
                    <p>امتیاز اضافه‌شده: {number(saleResult.earnedPoints)}</p>
                  ) : null}
                  {saleResult.pointsBalance !== undefined ? (
                    <p>
                      مانده امتیاز: {number(saleResult.pointsBalance)} ·{" "}
                      {saleResult.vipLevel ?? "Standard"}
                    </p>
                  ) : null}
                </div>
              ) : null}
              <p className="mt-2 text-xs">
                {saleResult.thankYouDeliveryStatus?.toLowerCase() === "sent"
                  ? "پیام تشکر و لینک رسید برای مشتری ارسال شد."
                  : saleResult.thankYouDeliveryStatus?.toLowerCase() ===
                      "skipped"
                    ? "ارسال پیام تشکر در تنظیمات غیرفعال است."
                    : saleResult.thankYouDeliveryError ||
                      "وضعیت ارسال پیام تشکر ثبت شد."}
              </p>
              <a
                className="mt-3 inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-xs font-black shadow-sm"
                href={saleResult.receiptUrl}
                rel="noreferrer"
                target="_blank"
              >
                <AppIcon name="document" size={15} /> مشاهده رسید
              </a>
            </div>
          ) : null}
        </aside>
      </section>
      {selectedLines.length ? (
        <button
          className={`fixed inset-x-3 bottom-[max(env(safe-area-inset-bottom),0.75rem)] z-50 flex items-center justify-between rounded-2xl px-4 py-3 text-right text-white shadow-xl xl:hidden ${mode === "sale" ? "bg-emerald-600 shadow-emerald-950/20" : "bg-slate-950 shadow-slate-950/25"}`}
          onClick={() => setMobileCheckoutOpen(true)}
          type="button"
        >
          <span><strong className="block text-sm">{selectedSummary}</strong><span className={`block text-[11px] ${mode === "sale" ? "text-emerald-100" : "text-slate-300"}`}>{mode === "sale" ? money(total) : "شماره مشتری و ارسال"}</span></span>
          <span className="flex items-center gap-1 rounded-xl bg-white px-3 py-2 text-xs font-black text-slate-900">ادامه <AppIcon name="arrow-left" size={16} /></span>
        </button>
      ) : null}

      {variantPicker ? (
        <RetailVariantPicker
          product={variantPicker}
          onClose={() => setVariantPicker(null)}
          onSelect={(variantId, branchId) => {
            const key = `${variantPicker.id}:${variantId}:${branchId}`;
            addSelectedLine({
              key,
              product: variantPicker,
              variantId,
              branchId,
            });
            setVariantPicker(null);
          }}
        />
      ) : null}

      {mode === "sale" ? (
        <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="font-black text-ink-950">آخرین فروش‌ها</h2>
              <p className="mt-1 text-xs text-slate-400">
                برای ثبت مانده، مرجوعی یا لغو، کارت فاکتور را باز کنید.
              </p>
            </div>
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">
              {number(invoicesQuery.data?.length ?? 0)} فاکتور
            </span>
          </div>
          {invoicesQuery.isLoading ? (
            <p className="py-8 text-center text-sm text-slate-400">
              در حال دریافت فاکتورها…
            </p>
          ) : null}
          {invoicesQuery.isError ? (
            <p className="mt-4 rounded-xl bg-rose-50 p-3 text-xs font-bold text-rose-700">
              دریافت فاکتورها ناموفق بود.
            </p>
          ) : null}
          <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {invoicesQuery.data?.map((invoice) => (
              <button
                className="rounded-2xl border border-slate-100 p-4 text-right transition hover:border-emerald-200 hover:shadow-sm"
                key={invoice.id ?? invoice.invoiceId}
                onClick={() => openInvoice(invoice)}
                type="button"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-black text-slate-800">
                      {invoice.customerName}
                    </p>
                    <p className="mt-1 font-sans text-[11px] text-slate-400">
                      {invoice.invoiceNumber}
                    </p>
                  </div>
                  <span
                    className={`rounded-full px-2 py-1 text-[10px] font-black ${invoice.status === "Completed" ? "bg-emerald-50 text-emerald-700" : invoice.status === "Cancelled" ? "bg-slate-100 text-slate-600" : "bg-amber-50 text-amber-700"}`}
                  >
                    {orderStatusLabels[invoice.status]}
                  </span>
                </div>
                <div className="mt-4 flex items-end justify-between">
                  <div>
                    <p className="text-base font-black text-emerald-700">
                      {money(
                        invoice.netAmount ?? invoice.totalAmount,
                        invoice.currency,
                      )}
                    </p>
                    <p
                      className={`mt-1 text-[11px] font-bold ${invoice.balanceDue > 0 ? "text-amber-700" : "text-slate-400"}`}
                    >
                      {invoice.balanceDue > 0
                        ? `مانده: ${money(invoice.balanceDue, invoice.currency)}`
                        : paymentStatusLabels[invoice.paymentStatus]}
                    </p>
                  </div>
                  <p className="text-[10px] text-slate-400">
                    {formatDateTime(invoice.createdAtUtc)}
                  </p>
                </div>
              </button>
            ))}
          </div>
          {!invoicesQuery.isLoading && !invoicesQuery.data?.length ? (
            <p className="py-10 text-center text-sm font-bold text-slate-400">
              هنوز فروش One‑Click ثبت نشده است.
            </p>
          ) : null}
        </section>
      ) : null}

      {missingMobileWarningOpen && typeof document !== "undefined"
        ? createPortal(
            <div className="fixed inset-0 z-[10020] flex items-end justify-center bg-slate-950/50 p-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] backdrop-blur-[1px] sm:items-center" role="dialog" aria-modal="true" aria-label="تأیید فروش بدون شماره مشتری">
              <section className="w-full max-w-md rounded-3xl bg-white p-5 shadow-2xl sm:p-6" dir="rtl">
                <div className="flex items-start gap-3">
                  <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-amber-100 text-amber-700"><AppIcon name="bell" size={20} /></span>
                  <div>
                    <h2 className="text-base font-black text-slate-950">شماره موبایل مشتری وارد نشده است</h2>
                    <p className="mt-2 text-xs font-medium leading-6 text-slate-600">با ثبت شماره موبایل، سابقه خرید، پیامک رسید و مزایای مشتری به پرونده او متصل می‌شود. اگر شماره را ندارید، می‌توانید فروش را بدون شماره ادامه دهید.</p>
                  </div>
                </div>
                <div className="mt-5 grid gap-2 sm:grid-cols-2">
                  <button className="rounded-xl bg-brand-600 px-4 py-3 text-sm font-black text-white" onClick={focusCustomerMobile} type="button">ثبت شماره مشتری</button>
                  <button className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700" disabled={sale.isPending} onClick={continueSaleWithoutMobile} type="button">ادامه بدون شماره</button>
                </div>
                <button className="mt-2 w-full rounded-xl px-4 py-2 text-xs font-bold text-slate-400" onClick={() => setMissingMobileWarningOpen(false)} type="button">انصراف</button>
              </section>
            </div>,
            document.body,
          )
        : null}

      {exitWarningOpen && typeof document !== "undefined"
        ? createPortal(
            <div
              aria-label="تأیید خروج از میز سریع"
              aria-modal="true"
              className="fixed inset-0 z-[10040] flex items-end justify-center bg-slate-950/55 p-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] backdrop-blur-[2px] sm:items-center"
              onMouseDown={() => setExitWarningOpen(false)}
              role="dialog"
            >
              <section
                className="w-full max-w-md rounded-3xl bg-white p-5 shadow-2xl dark:bg-slate-900 sm:p-6"
                dir="rtl"
                onMouseDown={(event) => event.stopPropagation()}
              >
                <div className="flex items-start gap-3">
                  <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300">
                    <AppIcon name="close" size={19} />
                  </span>
                  <div>
                    <h2 className="text-base font-black text-slate-950 dark:text-white">
                      از میز سریع خارج می‌شوید؟
                    </h2>
                    <p className="mt-2 text-xs font-medium leading-6 text-slate-600 dark:text-slate-300">
                      کالاهای انتخاب‌شده هنوز ثبت نشده‌اند و با خروج از این صفحه
                      از دست می‌روند.
                    </p>
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-2 gap-2">
                  <button
                    className="rounded-xl bg-rose-600 px-4 py-3 text-sm font-black text-white"
                    onClick={exitDesk}
                    type="button"
                  >
                    خروج از میز
                  </button>
                  <button
                    className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                    onClick={() => setExitWarningOpen(false)}
                    type="button"
                  >
                    ادامه کار
                  </button>
                </div>
              </section>
            </div>,
            document.body,
          )
        : null}

      {interactionTarget && typeof document !== "undefined"
        ? createPortal(
            <div className="fixed inset-0 z-[10030] flex justify-center bg-slate-950/45 xl:items-center xl:p-4" onMouseDown={() => { if (!productInteraction.isPending) setInteractionTarget(null); }} role="dialog" aria-modal="true" aria-label="ثبت سریع تعامل حضوری">
              <section className="h-full w-full overflow-y-auto overscroll-contain bg-white px-4 pb-[max(env(safe-area-inset-bottom),1.25rem)] pt-[max(env(safe-area-inset-top),0.75rem)] shadow-2xl dark:bg-slate-950 xl:h-auto xl:max-h-[92dvh] xl:max-w-lg xl:rounded-3xl xl:p-6" onMouseDown={(event) => event.stopPropagation()}>
                <div className="sticky top-0 z-10 -mx-4 flex items-start justify-between gap-4 border-b border-slate-100 bg-white/95 px-4 pb-4 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/95 xl:static xl:mx-0 xl:border-0 xl:bg-transparent xl:px-0 xl:pb-0"><div><p className="text-sm font-black text-violet-600 dark:text-violet-300">ثبت سریع تعامل حضوری</p><h2 className="mt-1 text-xl font-black text-ink-950 dark:text-white">{interactionTarget.name}</h2><p className="mt-2 text-xs leading-6 text-slate-500 dark:text-slate-400">نوع تعامل را انتخاب کنید. شماره موبایل اختیاری است؛ فقط در صورت تمایل به اتصال این تعامل به پرونده مشتری واردش کنید.</p></div><button aria-label="بستن" className="grid size-10 shrink-0 place-items-center rounded-xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300" disabled={productInteraction.isPending} onClick={() => setInteractionTarget(null)} type="button"><AppIcon name="close" size={18} /></button></div>
                <div className="mt-5 grid grid-cols-2 gap-2">{interactionSettingsQuery.data?.options.filter((option) => option.isActive).map((option) => <button className="min-h-14 rounded-2xl border border-violet-100 bg-violet-50 px-3 py-3 text-right text-sm font-black text-violet-800 transition hover:border-violet-400 hover:bg-violet-100 disabled:opacity-50 dark:border-violet-900 dark:bg-violet-950/50 dark:text-violet-200 dark:hover:bg-violet-950" disabled={productInteraction.isPending} key={option.key} onClick={() => productInteraction.mutate(option.key)} type="button">{option.title}</button>)}</div>
                <details className="mt-5 rounded-2xl bg-slate-50 p-3 dark:bg-slate-900"><summary className="cursor-pointer text-xs font-black text-slate-600 dark:text-slate-300">اتصال اختیاری به مشتری</summary><div className="mt-3 grid gap-3 sm:grid-cols-2"><label><span className="mb-1 block text-[11px] font-bold text-slate-500">موبایل مشتری</span><input className="input bg-white font-sans dark:bg-slate-950" dir="ltr" inputMode="tel" onFocus={(event) => window.setTimeout(() => event.currentTarget.scrollIntoView({ behavior: "smooth", block: "center" }), 120)} onChange={(event) => setInteractionMobile(event.target.value)} placeholder="0912…" value={interactionMobile} /></label><label><span className="mb-1 block text-[11px] font-bold text-slate-500">نام مشتری</span><input className="input bg-white dark:bg-slate-950" onFocus={(event) => window.setTimeout(() => event.currentTarget.scrollIntoView({ behavior: "smooth", block: "center" }), 120)} onChange={(event) => setInteractionCustomerName(event.target.value)} placeholder="اختیاری" value={interactionCustomerName} /></label></div></details>
                {productInteraction.isError ? <p className="mt-4 rounded-xl bg-rose-50 p-3 text-xs font-bold leading-6 text-rose-700">{getErrorMessage(productInteraction.error)}</p> : null}
              </section>
            </div>,
            document.body,
          )
        : null}

      {manageInvoice ? (
        <div
          className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/40 p-3 sm:p-8"
          onMouseDown={() => {
            if (!isPending) setManageInvoice(null);
          }}
        >
          <div
            className="mx-auto my-4 max-w-3xl rounded-3xl bg-white p-5 shadow-2xl sm:p-7"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-bold text-emerald-600">
                  مدیریت سفارش
                </p>
                <h2 className="mt-1 text-xl font-black text-ink-950">
                  {manageInvoice.customerName}
                </h2>
                <p className="mt-1 font-sans text-xs text-slate-400">
                  {manageInvoice.invoiceNumber}
                </p>
              </div>
              <button
                className="grid size-10 place-items-center rounded-xl bg-slate-100 text-slate-500"
                disabled={isPending}
                onClick={() => setManageInvoice(null)}
                type="button"
              >
                <AppIcon name="close" size={18} />
              </button>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-4">
              <div className="rounded-2xl bg-slate-50 p-3">
                <p className="text-[11px] font-bold text-slate-400">
                  مبلغ فاکتور
                </p>
                <p className="mt-1 text-sm font-black">
                  {money(manageInvoice.totalAmount, manageInvoice.currency)}
                </p>
              </div>
              <div className="rounded-2xl bg-emerald-50 p-3">
                <p className="text-[11px] font-bold text-emerald-700">
                  دریافتی
                </p>
                <p className="mt-1 text-sm font-black text-emerald-800">
                  {money(manageInvoice.paidAmount, manageInvoice.currency)}
                </p>
              </div>
              <div className="rounded-2xl bg-amber-50 p-3">
                <p className="text-[11px] font-bold text-amber-700">مانده</p>
                <p className="mt-1 text-sm font-black text-amber-800">
                  {money(manageInvoice.balanceDue, manageInvoice.currency)}
                </p>
              </div>
              <div className="rounded-2xl bg-rose-50 p-3">
                <p className="text-[11px] font-bold text-rose-700">مرجوعی</p>
                <p className="mt-1 text-sm font-black text-rose-800">
                  {money(manageInvoice.refundedAmount, manageInvoice.currency)}
                </p>
              </div>
            </div>
            <div className="mt-5 grid gap-5 lg:grid-cols-2">
              <section className="rounded-2xl border border-slate-200 p-4">
                <h3 className="font-black text-slate-800">ثبت دریافت جدید</h3>
                <p className="mt-1 text-xs leading-6 text-slate-400">
                  برای تسویه کامل یا دریافت بخشی از مانده فاکتور.
                </p>
                {manageInvoice.balanceDue > 0 &&
                manageInvoice.status !== "Cancelled" &&
                manageInvoice.status !== "Returned" ? (
                  <div className="mt-4 space-y-3">
                    <label>
                      <span className="mb-1.5 block text-xs font-bold text-slate-500">
                        مبلغ دریافت
                      </span>
                      <input
                        className="input font-sans"
                        dir="ltr"
                        inputMode="numeric"
                        max={manageInvoice.balanceDue}
                        min={1}
                        onChange={(event) =>
                          setPaymentAmount(
                            Math.max(0, Number(event.target.value) || 0),
                          )
                        }
                        value={paymentAmount || ""}
                      />
                    </label>
                    <label>
                      <span className="mb-1.5 block text-xs font-bold text-slate-500">
                        روش پرداخت
                      </span>
                      <select
                        className="input"
                        onChange={(event) =>
                          setPaymentMethod(
                            event.target.value as Exclude<
                              RetailPaymentMethod,
                              "Multiple"
                            >,
                          )
                        }
                        value={paymentMethod}
                      >
                        {selectablePaymentMethods.map((method) => (
                          <option key={method} value={method}>
                            {paymentLabels[method]}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span className="mb-1.5 block text-xs font-bold text-slate-500">
                        شماره پیگیری{" "}
                        <span className="font-normal">(اختیاری)</span>
                      </span>
                      <input
                        className="input font-sans"
                        dir="ltr"
                        onChange={(event) =>
                          setPaymentReference(event.target.value)
                        }
                        value={paymentReference}
                      />
                    </label>
                    <button
                      className="w-full rounded-xl bg-emerald-600 py-3 text-sm font-black text-white disabled:opacity-50"
                      disabled={
                        paymentAmount <= 0 ||
                        paymentAmount > manageInvoice.balanceDue ||
                        isPending
                      }
                      onClick={() => addPayment.mutate()}
                      type="button"
                    >
                      {addPayment.isPending ? "در حال ثبت…" : "ثبت دریافت"}
                    </button>
                  </div>
                ) : (
                  <p className="mt-4 rounded-xl bg-slate-50 p-3 text-xs font-bold text-slate-500">
                    برای این فاکتور مانده قابل دریافت وجود ندارد.
                  </p>
                )}
              </section>
              <section className="rounded-2xl border border-slate-200 p-4">
                <h3 className="font-black text-slate-800">
                  مرجوعی کالا یا خدمت
                </h3>
                <p className="mt-1 text-xs leading-6 text-slate-400">
                  مبلغ مرجوعی با درنظرگرفتن تخفیف، خودکار محاسبه می‌شود.
                </p>
                {manageInvoice.status === "Completed" ||
                manageInvoice.status === "PartiallyReturned" ? (
                  <div className="mt-4 space-y-3">
                    {manageInvoice.items.map((item) => {
                      const returned = (manageInvoice.returns ?? [])
                        .filter((entry) => entry.invoiceItemId === item.id)
                        .reduce((sum, entry) => sum + entry.quantity, 0);
                      const remaining = item.quantity - returned;
                      return (
                        <div
                          className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 p-3"
                          key={item.id ?? item.catalogItemId}
                        >
                          <div className="min-w-0">
                            <p className="truncate text-xs font-black text-slate-700">
                              {item.name}
                            </p>
                            <p className="mt-1 text-[11px] text-slate-400">
                              باقی‌مانده: {number(remaining)} از{" "}
                              {number(item.quantity)}
                            </p>
                          </div>
                          <input
                            className="w-20 rounded-lg border border-slate-200 px-2 py-2 text-center font-sans text-sm"
                            disabled={remaining <= 0}
                            dir="ltr"
                            inputMode="numeric"
                            max={remaining}
                            min={0}
                            onChange={(event) =>
                              setReturnQuantities((current) => ({
                                ...current,
                                [item.id ?? ""]: Math.min(
                                  remaining,
                                  Math.max(0, Number(event.target.value) || 0),
                                ),
                              }))
                            }
                            value={returnQuantities[item.id ?? ""] || ""}
                          />
                        </div>
                      );
                    })}
                    <label>
                      <span className="mb-1.5 block text-xs font-bold text-slate-500">
                        روش استرداد
                      </span>
                      <select
                        className="input"
                        onChange={(event) =>
                          setReturnMethod(
                            event.target.value as Exclude<
                              RetailPaymentMethod,
                              "Multiple"
                            >,
                          )
                        }
                        value={returnMethod}
                      >
                        {selectablePaymentMethods.map((method) => (
                          <option key={method} value={method}>
                            {paymentLabels[method]}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span className="mb-1.5 block text-xs font-bold text-slate-500">
                        علت مرجوعی{" "}
                        <span className="font-normal">(اختیاری)</span>
                      </span>
                      <input
                        className="input"
                        onChange={(event) =>
                          setReturnReason(event.target.value)
                        }
                        value={returnReason}
                      />
                    </label>
                    <button
                      className="w-full rounded-xl bg-rose-600 py-3 text-sm font-black text-white disabled:opacity-50"
                      disabled={
                        !Object.values(returnQuantities).some(
                          (quantity) => quantity > 0,
                        ) || isPending
                      }
                      onClick={() => returnSale.mutate()}
                      type="button"
                    >
                      {returnSale.isPending
                        ? "در حال ثبت…"
                        : "ثبت مرجوعی و استرداد"}
                    </button>
                  </div>
                ) : (
                  <p className="mt-4 rounded-xl bg-slate-50 p-3 text-xs font-bold text-slate-500">
                    این سفارش در وضعیت مرجوع‌شده یا لغوشده است.
                  </p>
                )}
              </section>
            </div>
            {manageInvoice.status === "Completed" &&
            manageInvoice.paidAmount === 0 &&
            manageInvoice.refundedAmount === 0 ? (
              <div className="mt-5 border-t border-slate-100 pt-5">
                <button
                  className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-xs font-black text-rose-700 disabled:opacity-50"
                  disabled={isPending}
                  onClick={() => {
                    if (window.confirm("این سفارش لغو شود؟"))
                      cancelSale.mutate();
                  }}
                  type="button"
                >
                  {cancelSale.isPending
                    ? "در حال لغو…"
                    : "لغو سفارش پرداخت‌نشده"}
                </button>
              </div>
            ) : null}
            {addPayment.isError || returnSale.isError || cancelSale.isError ? (
              <p className="mt-4 rounded-xl bg-rose-50 p-3 text-xs font-bold leading-6 text-rose-700">
                {getErrorMessage(
                  addPayment.error ?? returnSale.error ?? cancelSale.error,
                )}
              </p>
            ) : null}
            <a
              className="mt-5 inline-flex rounded-xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700"
              href={manageInvoice.receiptUrl}
              rel="noreferrer"
              target="_blank"
            >
              مشاهده رسید مشتری
            </a>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const RetailVariantPicker = ({
  product,
  onClose,
  onSelect,
}: {
  product: RetailProduct;
  onClose: () => void;
  onSelect: (variantId: string, branchId: string) => void;
}): React.ReactElement => {
  const [variantId, setVariantId] = useState(
    product.variants.find((variant) =>
      variant.stocks.some((stock) => stock.onHandQuantity > 0),
    )?.id ?? "",
  );
  const variant = product.variants.find((item) => item.id === variantId);
  const [branchId, setBranchId] = useState(
    variant?.stocks.find((stock) => stock.onHandQuantity > 0)?.branchId ?? "",
  );
  const changeVariant = (nextVariantId: string): void => {
    const next = product.variants.find((item) => item.id === nextVariantId);
    setVariantId(nextVariantId);
    setBranchId(
      next?.stocks.find((stock) => stock.onHandQuantity > 0)?.branchId ?? "",
    );
  };
  const availableBranches =
    variant?.stocks.filter((stock) => stock.onHandQuantity > 0) ?? [];
  return (
    <div className="fixed inset-0 z-50 grid place-items-end bg-slate-950/45 sm:place-items-center sm:p-4">
      <button
        aria-label="بستن"
        className="absolute inset-0"
        onClick={onClose}
        type="button"
      />
      <section className="relative z-10 w-full rounded-t-3xl bg-white p-5 shadow-2xl sm:max-w-md sm:rounded-3xl sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-bold text-violet-600">
              انتخاب واریانت برای فروش
            </p>
            <h2 className="mt-1 text-lg font-black text-ink-950">
              {product.name}
            </h2>
            <p className="mt-1 text-xs leading-6 text-slate-400">
              واریانت و شعبه را انتخاب کنید تا فقط از موجودی همان شعبه کم شود.
            </p>
          </div>
          <button
            className="grid size-9 place-items-center rounded-xl bg-slate-100 text-slate-500"
            onClick={onClose}
            type="button"
          >
            <AppIcon name="close" size={18} />
          </button>
        </div>
        <label className="mt-5 block">
          <span className="mb-2 block text-xs font-bold text-slate-500">
            رنگ / سایز / واریانت
          </span>
          <select
            className="input bg-white"
            onChange={(event) => changeVariant(event.target.value)}
            value={variantId}
          >
            <option value="">انتخاب واریانت</option>
            {product.variants.map((item) => (
              <option
                disabled={item.totalStock <= 0}
                key={item.id}
                value={item.id}
              >
                {item.name} — موجودی {number(item.totalStock)}
              </option>
            ))}
          </select>
        </label>
        <label className="mt-4 block">
          <span className="mb-2 block text-xs font-bold text-slate-500">
            شعبه فروش
          </span>
          <select
            className="input bg-white"
            disabled={!variantId}
            onChange={(event) => setBranchId(event.target.value)}
            value={branchId}
          >
            <option value="">انتخاب شعبه</option>
            {availableBranches.map((stock) => (
              <option key={stock.branchId} value={stock.branchId}>
                {stock.branchName} — موجودی {number(stock.onHandQuantity)}
              </option>
            ))}
          </select>
        </label>
        {variant ? (
          <div className="mt-4 rounded-2xl bg-violet-50 p-3 text-xs font-bold text-violet-800">
            <div className="flex justify-between">
              <span>قیمت فروش</span>
              <span>
                {money(
                  variant.priceOverride ?? product.price,
                  product.currency,
                )}
              </span>
            </div>
            {variant.sku || variant.barcode ? (
              <p className="mt-2 font-sans text-[11px] text-violet-600">
                {variant.barcode || variant.sku}
              </p>
            ) : null}
          </div>
        ) : null}
        <button
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-violet-600 py-3 text-sm font-black text-white disabled:opacity-50"
          disabled={!variantId || !branchId}
          onClick={() => onSelect(variantId, branchId)}
          type="button"
        >
          <AppIcon name="plus" size={18} />
          افزودن به فروش
        </button>
      </section>
    </div>
  );
};
